/* eslint-disable no-script-url */
/* eslint-disable consistent-return */
import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useDataLayerAndAnalyticsLinks,
  useSubmitSavedApplication,
  useUpdatePOIType,
  useErrorTrackingAndUpdateStore,
} from 'hooks';
import { Container, Text } from 'basis';
import {
  useStoreValue,
  SHARE_BANK_STATEMENT,
  UPLOAD_PAYSLIPS,
  UPLOAD_DOCUMENTS,
  POITYPE_BANK_CONNECT,
  POITYPE_PAYSLIP_UPLOAD,
  POITYPE_BANK_STATEMENT_UPLOAD,
  POITYPE_DOC_UPLOAD,
  PROVIDE_PAYSLIPS,
  PROVIDE_DOCUMENTS,
  ONLINE_BANK_CONNECT,
  UPLOAD_YOUR_DOCUMENTS,
  UPLOAD_BANK_STATEMENTS,
  PROVIDE_BANK_STATEMENTS,
} from 'store';
import { LayoutPage, LayoutContent, InformationCard, Errors, SaveAndCompleteLaterButton, PdfIcon } from 'components';
import ShareBankStatement from 'images/vi_share_your_bank_statement.svg';
import LockIcon from 'images/icons/lock2.svg';
import DocumentUpload from 'images/vi_document_upload_au.svg';
import UploadDocuments from 'images/vi_upload_other_documents.svg';
import UploadPayslips from 'images/vi_upload_your_payslips.svg';
import { isEligibleForBankConnectAndBankStatementAu } from '_config';
import { isEligibleForBankConnectAndBankStatementNz } from '_config/_constants';
import { byCountry, isAU, isNZ } from 'utils/byConfig';
import styled from '@emotion/styled';
import { device } from 'media';
import { isFeatureOn } from 'featureToggles';
import {
  ShareBankStatementContent,
  UploadPayslipsContent,
  UploadBankStatementsContent,
  UploadDocumentsContent,
  OnlineBankConnectContent,
  DocumentUploadContentAU,
} from './VerifyIncomeCards.content';
import { ShareBankStatementModal } from './ShareBankStatement.modal';
import { OnlineBankConnectModal } from './OnlineBankConnect.modal';
import { PoiMethods } from './PoiMethods';

const HR = styled.hr`
  margin: 40px 0;
  border: none;
  border-top: 2px solid #7f7f7f;
  color: #333;
  overflow: visible;
  text-align: center;
  height: 5px;

  :after {
    background: #7f7f7f;
    border-radius: 50%;
    letter-spacing: -0.35px;
    text-edge: cap;
    font-size: 16px;
    content: 'or';
    padding: 9px 11px;
    position: relative;
    top: -13px;
    color: var(--blacks-and-greys-black-03, #f8f8f8);
  }
`;
const ButtonContainer = styled.div`
  margin: 32px 0;
  @media ${device.mobileOrSmaller} {
    button {
      width: 100%;
    }
  }
`;

const VerifyIncome = () => {
  const [storeState, updateStore] = useStoreValue();
  const [openModal, setOpenModal] = useState('');
  const history = useHistory();

  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const { handleErrorCallback } = useErrorTrackingAndUpdateStore();

  const handleOpenModal = useCallback(type => {
    setOpenModal(type);
  }, []);

  const handleShareBankStatement = () => {
    updateStore({
      activeStep: '/verify-ficonnect',
      poiStartDate: new Date().toISOString(),
      incomeVerificationType: POITYPE_BANK_CONNECT,
    });
    trackEventSender({
      category: 'application',
      action: 'application-navigation',
      location: 'verify-income',
      label: 'Share bank statement',
    });
    history.push('/verify-ficonnect');
  };

  const goToProcessingPage = () => {
    updateStore({
      applicationErrors: null,
      activeStep: '/processing',
    });

    history.push('/processing');
  };

  const goToReplayIncomePage = useCallback(() => {
    updateStore({
      applicationErrors: null,
      activeStep: '/confirm-income',
    });

    history.push('/confirm-income');
  }, [history, updateStore]);
  const goToDocumentUploadPage = useCallback(() => {
    updateStore({
      applicationErrors: null,
      activeStep: '/verify-document-upload',
    });

    history.push('/verify-document-upload');
  }, [history, updateStore]);

  const { submit, loading } = useSubmitSavedApplication({
    storeState,
    onSuccess: goToProcessingPage,
    onError: handleErrorCallback,
  });
  const { updatePOI } = useUpdatePOIType({
    storeState,
    onSuccess: goToDocumentUploadPage,
    onError: handleErrorCallback,
  });
  const handleSubmitOtherWays = useCallback(
    ({ label }) => {
      trackEventSender({
        category: 'application',
        action: byCountry({
          AU: 'application-submitted',
          NZ: 'application-navigation',
        }),
        location: 'verify-income',
        label,
      });

      const incomeVerificationType =
        label === UPLOAD_PAYSLIPS
          ? POITYPE_PAYSLIP_UPLOAD
          : label === UPLOAD_BANK_STATEMENTS
          ? POITYPE_BANK_STATEMENT_UPLOAD
          : POITYPE_DOC_UPLOAD;
      updateStore({ incomeVerificationType });
      if (isFeatureOn('incomeReplay')) {
        goToReplayIncomePage();
      } else if (isAU() || (isNZ() && isFeatureOn('nzDmsUpload'))) {
        goToDocumentUploadPage();
      } else {
        // when NZ with DMS feature off
        submit({ incomeVerificationType });
      }
    },
    [goToReplayIncomePage, submit, trackEventSender, updateStore, goToDocumentUploadPage],
  );

  const handleUploadDocuments = useCallback(() => handleSubmitOtherWays({ label: UPLOAD_DOCUMENTS }), [
    handleSubmitOtherWays,
  ]);

  const handleUploadYourDocuments = useCallback(() => handleSubmitOtherWays({ label: UPLOAD_YOUR_DOCUMENTS }), [
    handleSubmitOtherWays,
  ]);

  const handleUploadPayslips = useCallback(() => handleSubmitOtherWays({ label: UPLOAD_PAYSLIPS }), [
    handleSubmitOtherWays,
  ]);
  const handleUploadBankStatements = useCallback(() => handleSubmitOtherWays({ label: UPLOAD_BANK_STATEMENTS }), [
    handleSubmitOtherWays,
  ]);

  const closeAllModals = useCallback(() => setOpenModal(''), [setOpenModal]);

  const showBankConnectOption = byCountry({
    AU: isEligibleForBankConnectAndBankStatementAu(storeState.acquisition.employmentDetails),
    NZ: isEligibleForBankConnectAndBankStatementNz(storeState.acquisition.employmentDetails.employmentStatus),
  });

  const showBankStatementOption = byCountry({
    AU: isFeatureOn('auDmsUpload') && isFeatureOn('auBankStatementOcr') && showBankConnectOption,
    NZ: isFeatureOn('nzBankStatementOcr') && showBankConnectOption,
  });

  if (storeState.applicationErrors) {
    const retry = () => {
      updateStore({
        applicationErrors: null,
      });

      if (isFeatureOn('incomeReplay')) {
        goToReplayIncomePage();
      } else if (isAU() || isNZ()) {
        updatePOI(storeState.incomeVerificationType);
      } else {
        submit({ incomeVerificationType: storeState.incomeVerificationType });
      }
    };

    return (
      <LayoutPage hideBackButton>
        <Errors retry={retry} retrying={loading} />
      </LayoutPage>
    );
  }

  const VerifyIncomeOptionsNzV1 = () => {
    return (
      <Container>
        {showBankConnectOption && (
          <>
            <Container>
              <Text>
                <strong>The fastest way to get an outcome</strong>
              </Text>
            </Container>
            <InformationCard
              icon={ShareBankStatement}
              iconAlt={SHARE_BANK_STATEMENT}
              title="Share your bank statement"
              btnText="Share bank statement"
              onClick={handleShareBankStatement}
              testId="share_bank_statement"
              variant="primary"
            >
              <ShareBankStatementContent trackEventSender={trackEventSender} handleOpenModal={handleOpenModal} />
            </InformationCard>
            <Container padding="4 0 0 0">
              <Text>Other ways to prove your income</Text>
            </Container>
          </>
        )}

        <InformationCard
          icon={UploadPayslips}
          iconAlt={UPLOAD_PAYSLIPS}
          title="Upload your payslips"
          onClick={handleUploadPayslips}
          btnText={isFeatureOn('dynamicCreditLimit') ? PROVIDE_PAYSLIPS : UPLOAD_PAYSLIPS}
          loading={storeState.incomeVerificationType === POITYPE_PAYSLIP_UPLOAD && loading}
          testId="upload_payslips"
        >
          <UploadPayslipsContent trackEventSender={trackEventSender} handleOpenModal={handleOpenModal} />
        </InformationCard>

        {showBankStatementOption && (
          <InformationCard
            iconComponent={<PdfIcon height="50" width="47" viewBox="0 0 47 50" />}
            iconAlt={UPLOAD_DOCUMENTS}
            title="Upload bank statements"
            subTitle="(PDF file format only)"
            onClick={handleUploadBankStatements}
            btnText={isFeatureOn('dynamicCreditLimit') ? PROVIDE_BANK_STATEMENTS : UPLOAD_BANK_STATEMENTS}
            loading={storeState.incomeVerificationType === POITYPE_DOC_UPLOAD && loading}
            testId="upload_bank_statements"
          >
            <UploadBankStatementsContent trackEventSender={trackEventSender} handleOpenModal={handleOpenModal} />
          </InformationCard>
        )}
        <InformationCard
          icon={UploadDocuments}
          iconAlt={UPLOAD_DOCUMENTS}
          title="Upload other documents"
          onClick={handleUploadDocuments}
          btnText={isFeatureOn('dynamicCreditLimit') ? PROVIDE_DOCUMENTS : UPLOAD_DOCUMENTS}
          loading={storeState.incomeVerificationType === POITYPE_DOC_UPLOAD && loading}
          testId="upload_documents"
        >
          <UploadDocumentsContent trackEventSender={trackEventSender} handleOpenModal={handleOpenModal} />
        </InformationCard>
      </Container>
    );
  };

  const VerifyIncomeOptions = () => {
    return isFeatureOn('auDmsUpload') || isNZ() ? (
      <PoiMethods
        showBankConnectOption={showBankConnectOption}
        showBankStatementOption={showBankStatementOption}
        onClickBankConnect={handleShareBankStatement}
        onClickPayslipUpload={handleUploadPayslips}
        onClickBankStatementsUpload={handleUploadBankStatements}
        onClickDocUpload={handleUploadYourDocuments}
      />
    ) : (
      <Container padding="0" margin="6 0 0 0">
        <Container bg="grey.t03" padding="4" padding-md="10">
          {showBankConnectOption && (
            <>
              <InformationCard
                icon={LockIcon}
                iconAlt={ONLINE_BANK_CONNECT}
                title={ONLINE_BANK_CONNECT}
                btnText="Verify online"
                onClick={handleShareBankStatement}
                testId="verify_online"
                variant="primary"
              >
                <OnlineBankConnectContent trackEventSender={trackEventSender} handleOpenModal={handleOpenModal} />
              </InformationCard>
              <HR />
            </>
          )}

          <InformationCard
            icon={DocumentUpload}
            iconAlt={UPLOAD_DOCUMENTS}
            title="Document Upload"
            onClick={handleUploadYourDocuments}
            btnText={UPLOAD_YOUR_DOCUMENTS}
            loading={storeState.incomeVerificationType === POITYPE_DOC_UPLOAD && loading}
            testId="upload_your_documents"
          >
            <DocumentUploadContentAU trackEventSender={trackEventSender} />
          </InformationCard>
        </Container>
      </Container>
    );
  };
  const DynamicVerifyIncomeOptions = () => {
    return isAU() || isNZ() ? <VerifyIncomeOptions /> : <VerifyIncomeOptionsNzV1 />;
  };
  return (
    <LayoutPage>
      <Container margin="0 0 13 0">
        <LayoutContent rowsGap="0">
          <Container padding="0 0 6 0" padding-md="0 0 8 0">
            <Text>
              {byCountry({
                AU: "You're almost there! We just need to verify a few things before we finalise your application.",
                NZ: isNZ() ? (
                  <>
                    Please select <strong>one</strong> of the following options to verify your income.
                  </>
                ) : (
                  'NZ legislation requires us to verify your income.'
                ),
              })}
            </Text>
            <Text />
          </Container>

          {isAU() && (
            <Container>
              <Text>
                {isFeatureOn('auDmsUpload') ? (
                  <>
                    Please select <strong>one</strong> of the following options to verify your income.
                  </>
                ) : (
                  <>
                    Please select <strong>one</strong> of the following options to verify your information.
                  </>
                )}
              </Text>
            </Container>
          )}
          <DynamicVerifyIncomeOptions />
          {isAU() ||
            (isNZ() && (
              <ButtonContainer>
                <SaveAndCompleteLaterButton />
              </ButtonContainer>
            ))}
        </LayoutContent>
      </Container>

      <ShareBankStatementModal isOpen={openModal === SHARE_BANK_STATEMENT} onClose={closeAllModals} />
      <OnlineBankConnectModal isOpen={openModal === ONLINE_BANK_CONNECT} onClose={closeAllModals} />
    </LayoutPage>
  );
};

export { VerifyIncome };
